import { Controller } from "@hotwired/stimulus"

/**
 * Progressive Disclosure Controller
 *
 * Handles progressive reveal of form sections:
 * - Shows additional business fields after verification completes
 * - Provides smooth transitions and accessibility announcements
 * - Integrates with business-verification controller
 */
export default class extends Controller {
  static targets = ["additionalField", "additionalFieldsHeader"]

  connect() {
    this.element.addEventListener('business-verification:success', () => {
      this.revealAdditionalFields()
    })
  }

  /**
   * Reveal additional business detail fields after verification
   */
  revealAdditionalFields() {
    // Reveal header if present
    if (this.hasAdditionalFieldsHeaderTarget) {
      this.additionalFieldsHeaderTarget.classList.remove('hidden')
      this.additionalFieldsHeaderTarget.classList.add('animate-fadeIn')
    }

    // Reveal all additional field elements
    if (this.hasAdditionalFieldTarget) {
      this.additionalFieldTargets.forEach((field, index) => {
        // Stagger the reveal for a smoother effect
        setTimeout(() => {
          field.classList.remove('hidden')
          field.classList.add('animate-fadeIn')
        }, index * 50) // 50ms delay between each field
      })

      // Scroll to first additional field after reveals complete
      setTimeout(() => {
        this.scrollToAdditionalFields()
      }, this.additionalFieldTargets.length * 50 + 100)

      // Announce to screen readers
      this.announceToScreenReader('Additional business details section now available')
    }
  }

  /**
   * Scroll to additional fields section
   */
  scrollToAdditionalFields() {
    // Scroll to the header if it exists, otherwise to the first field
    const scrollTarget = this.hasAdditionalFieldsHeaderTarget
      ? this.additionalFieldsHeaderTarget
      : this.additionalFieldTargets[0]

    if (!scrollTarget) return

    const offset = this.isMobile() ? 80 : 120
    const elementPosition = scrollTarget.getBoundingClientRect().top + window.pageYOffset
    const offsetPosition = elementPosition - offset

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    })
  }

  /**
   * Check if viewport is mobile
   */
  isMobile() {
    return window.innerWidth < 768 // Tailwind 'md' breakpoint
  }

  /**
   * Announce message to screen readers
   */
  announceToScreenReader(message) {
    const announcement = document.createElement('div')
    announcement.setAttribute('role', 'status')
    announcement.setAttribute('aria-live', 'polite')
    announcement.className = 'sr-only'
    announcement.textContent = message

    document.body.appendChild(announcement)

    // Remove after announcement
    setTimeout(() => {
      document.body.removeChild(announcement)
    }, 1000)
  }
}
